import { useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
    type Layout,
    type LayoutChangedMeta,
    useDefaultLayout,
    usePanelRef,
} from "react-resizable-panels";
import { AgentSkillKind } from "@common/types/agentSkill";
import { OrganizationAction } from "@common/userOrganizationRoles";
import {
    MainInterface,
    type MainInterfaceProps,
} from "@components/mainInterface/MainInterface";
import type { Breadcrumb } from "@components/mainInterface/MainInterfaceHeader";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { HStack } from "@design-system/HStack/HStack";
import { Link } from "@design-system/Link/Link";
import { Loader } from "@design-system/Loader/Loader";
import { Markdown } from "@design-system/Markdown/Markdown";
import { hasOpenMenu } from "@design-system/Menu/hasOpenMenu";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useHasOpenModal } from "@design-system/overlay/openModalLock";
import { PageSection } from "@design-system/PageSection/PageSection";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import { Tag } from "@design-system/Tag/Tag";
import { VStack } from "@design-system/VStack/VStack";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useAgentSkill } from "@api/agentSkill.api";
import { useOrganizationClients } from "@components/organizationView/activity/useOrganizationClients";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import {
    TaskCloseButton,
    TaskPaneToggleButton,
} from "@components/tasks/TaskBody/TaskBodyHeaderControls";
import type { AgentSkillPanelTab } from "./agentSkillChat";
import {
    AGENT_SKILL_CATEGORY_LABELS,
    AGENT_SKILL_LOAD_MODE_LABELS,
    CUSTOMIZED_LABEL,
    FIRM_SCOPE_LABEL,
    KICK_DEFAULT_LABEL,
    KICK_DEFAULT_OUTDATED_HINT,
    KICK_DEFAULT_OUTDATED_LABEL,
    RESET_TO_DEFAULT_LABEL,
    VIEW_KICK_DEFAULT_LABEL,
} from "./agentSkillCopy";
import { AgentSkillEditorForm } from "./AgentSkillEditorForm";
import { AgentSkillFormValues, getEditAgentSkillForm } from "./agentSkillForm";
import { AgentSkillSidePanel } from "./AgentSkillSidePanel";
import {
    useAgentSkillLifecycle,
    useAgentSkillUpdate,
} from "./useAgentSkillLifecycle";
import styles from "./AgentSkills.module.scss";

const SKILLS_PATH = "/automation/skills";
const PANE_ID = "agent-skill-pane";
const PANE_MIN_SIZE = "340px";

export const agentSkillPagePath = (skillId: string) =>
    `${SKILLS_PATH}/${skillId}`;

/** The skill page's frame: the trail ends with the loaded skill's title. */
export const AgentSkillMainInterface = (
    props: Omit<
        MainInterfaceProps,
        "breadcrumbs" | "contained" | "containerClassName"
    >,
) => {
    const { skillId = null } = useParams<{ skillId: string }>();
    const { data: skill } = useAgentSkill(skillId);
    const breadcrumbs = useMemo<Breadcrumb[]>(
        () => [
            { label: "Automation" },
            { label: "Skills", to: SKILLS_PATH },
            ...(skill ? [{ label: skill.title }] : []),
        ],
        [skill],
    );

    return (
        <MainInterface
            {...props}
            breadcrumbs={breadcrumbs}
            contained
            containerClassName={styles.main}
        >
            <AgentSkillPage />
        </MainInterface>
    );
};

export const AgentSkillPage = () => {
    const { skillId = null } = useParams<{ skillId: string }>();
    const navigate = useStateAwareNavigate();
    // The list's Edit details action lands here already editing.
    const { state: locationState } = useLocation();
    const shouldOpenEditorOnArrival =
        locationState !== null &&
        typeof locationState === "object" &&
        "editSkill" in locationState &&
        locationState.editSkill === true;
    const [editorBody, setEditorBody] = useState<string | null>(null);
    const [editorWarnings, setEditorWarnings] = useState<string[]>([]);
    const [isEditorOpen, setIsEditorOpen] = useState(shouldOpenEditorOnArrival);
    const [tab, setTab] = useState<AgentSkillPanelTab>("chat");
    const canManage = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_AGENT_SKILLS,
    );
    const { data: skill, isFetched, isError } = useAgentSkill(skillId);
    const { data: workspaces } = useOrganizationClients();
    const updateAgentSkill = useAgentSkillUpdate();
    const {
        enable,
        disable,
        restore,
        requestDelete,
        requestReset,
        deleteModal,
        resetModal,
    } = useAgentSkillLifecycle({
        onDeleted: () => navigate(SKILLS_PATH),
        onReset: (global) => navigate(agentSkillPagePath(global.id)),
    });
    const { defaultLayout, onLayoutChanged } = useDefaultLayout({
        id: "agent-skill-panel-group",
        storage: localStorage,
        onlySaveAfterUserInteractions: true,
    });
    const paneRef = usePanelRef();
    const [isPaneCollapsed, setIsPaneCollapsed] = useState(false);
    const hasOpenModal = useHasOpenModal();
    const isEscapeActive = !hasOpenModal && !isEditorOpen;
    const closePage = () => navigate(SKILLS_PATH);
    useKeyboardCommand({
        keys: "Escape",
        callback: closePage,
        options: {
            enabled: isEscapeActive,
            preventDefault: isEscapeActive,
            ignoreEventWhen: hasOpenMenu,
            description: "Close skill",
        },
    });
    const togglePane = () => {
        const pane = paneRef.current;
        if (pane?.isCollapsed()) {
            pane.expand();
        } else {
            pane?.collapse();
        }
    };
    // A collapsed pane is not a layout to come back to.
    const saveExpandedLayout = (layout: Layout, meta: LayoutChangedMeta) => {
        if ((layout[PANE_ID] ?? 0) > 0) {
            onLayoutChanged(layout, meta);
        }
    };
    if (!isFetched) {
        return <Loader />;
    }
    if (isError || !skill || skill.isArchived) {
        return (
            <EmptyState
                header="Skill not found"
                ctaText="Back to skills"
                action={() => navigate(SKILLS_PATH)}
            />
        );
    }

    const isKick = skill.kind === AgentSkillKind.KICK;
    const clientName = skill.workspaceId
        ? (workspaces?.find(
              (workspace) => String(workspace.id) === skill.workspaceId,
          )?.name ?? "Client")
        : null;
    const target = { skillId: skill.id, title: skill.title };

    // The first edit of a Kick default forks the firm's override: the saved
    // row has a new id, so the page moves to it.
    // A page move unmounts the drawer, so warnings from that save are kept
    // here and handed to the drawer that remounts on the override.
    const handleEdit = async (values: AgentSkillFormValues) => {
        const updated = await updateAgentSkill({ skill, values });
        if (updated && updated.id !== skill.id) {
            setEditorBody(null);
            setEditorWarnings(updated.warnings);
            navigate(agentSkillPagePath(updated.id));
        }
        return updated?.warnings;
    };

    const openEditor = (body: string | null) => {
        setEditorBody(body);
        setEditorWarnings([]);
        setIsEditorOpen(true);
    };

    const closeEditor = () => {
        setEditorWarnings([]);
        setIsEditorOpen(false);
    };

    return (
        <PageSection className={styles.page} noPaddingInline>
            <div className={styles.titleBar}>
                <div className={styles.titleBarStart}>
                    <BreakableText
                        text={skill.title}
                        className={styles.title}
                        showTooltip
                    />
                    <HStack gap={8} align="center" className={styles.titleTags}>
                        {skill.isDefault && (
                            <Tag color="neutral" size="sm" outline>
                                {KICK_DEFAULT_LABEL}
                            </Tag>
                        )}
                        {skill.isCustomized && (
                            <Tag color="neutral" size="sm" outline>
                                {CUSTOMIZED_LABEL}
                            </Tag>
                        )}
                        <Tag color="neutral" size="sm" outline>
                            {clientName ?? FIRM_SCOPE_LABEL}
                        </Tag>
                        <Tag color="neutral" size="sm" outline>
                            {AGENT_SKILL_CATEGORY_LABELS[skill.category]}
                        </Tag>
                        <Tag color="neutral" size="sm" outline>
                            {AGENT_SKILL_LOAD_MODE_LABELS[skill.loadMode]}
                        </Tag>
                        {!skill.isEnabled && (
                            <Tag color="neutral" size="sm">
                                Disabled
                            </Tag>
                        )}
                    </HStack>
                </div>
                <HStack gap={8} align="center" className={styles.titleBarEnd}>
                    <TaskPaneToggleButton
                        isPaneCollapsed={isPaneCollapsed}
                        hasUnreadMessages={false}
                        onClick={togglePane}
                    />
                    <TaskCloseButton onClick={closePage} />
                </HStack>
            </div>
            <ResizablePanelGroup
                orientation="horizontal"
                defaultLayout={defaultLayout}
                onLayoutChanged={saveExpandedLayout}
                className={styles.panelGroup}
            >
                <ResizablePanel id="agent-skill-body">
                    {isEditorOpen ? (
                        <AgentSkillEditorForm
                            key={editorBody ?? "current"}
                            className={styles.document}
                            submitLabel="Save"
                            buttonSize="md"
                            initialValues={{
                                ...getEditAgentSkillForm(skill),
                                ...(editorBody === null
                                    ? {}
                                    : { body: editorBody }),
                            }}
                            clientName={clientName}
                            kind={skill.kind}
                            tools={skill.tools}
                            initialWarnings={editorWarnings}
                            onSubmit={handleEdit}
                            onClose={closeEditor}
                            renderActions={(actions) => (
                                <CommandIsland
                                    sticky
                                    className={styles.documentIsland}
                                >
                                    {actions}
                                </CommandIsland>
                            )}
                        />
                    ) : (
                        <div className={styles.document}>
                            <VStack gap={8}>
                                <span className="text-secondary">
                                    {skill.description}
                                </span>
                                {skill.isDefaultOutdated && (
                                    <HStack
                                        gap={8}
                                        align="center"
                                        className={styles.outdatedBanner}
                                    >
                                        <Tag color="accent" size="sm">
                                            {KICK_DEFAULT_OUTDATED_LABEL}
                                        </Tag>
                                        <span>
                                            {KICK_DEFAULT_OUTDATED_HINT}
                                        </span>
                                        {skill.defaultSkillId && (
                                            <Link
                                                render={
                                                    <StateAwareLink
                                                        to={agentSkillPagePath(
                                                            skill.defaultSkillId,
                                                        )}
                                                    />
                                                }
                                            >
                                                {VIEW_KICK_DEFAULT_LABEL}
                                            </Link>
                                        )}
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            disabled={!canManage}
                                            onClick={() => requestReset(target)}
                                        >
                                            {RESET_TO_DEFAULT_LABEL}
                                        </Button>
                                    </HStack>
                                )}
                            </VStack>

                            <div className={styles.body}>
                                <Markdown>{skill.body}</Markdown>
                            </div>

                            <CommandIsland
                                sticky
                                className={styles.documentIsland}
                            >
                                <Button
                                    variant="primary"
                                    size="md"
                                    disabled={!canManage}
                                    onClick={() => openEditor(null)}
                                >
                                    Update text
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={() => setTab("versions")}
                                >
                                    Version history
                                </Button>
                                <span className="text-secondary">
                                    Version {skill.currentVersion}
                                </span>
                                <HStack gap={8} className={styles.islandEnd}>
                                    {isKick ? (
                                        skill.isCustomized && (
                                            <Button
                                                variant="secondary"
                                                size="md"
                                                disabled={!canManage}
                                                onClick={() =>
                                                    requestReset(target)
                                                }
                                            >
                                                {RESET_TO_DEFAULT_LABEL}
                                            </Button>
                                        )
                                    ) : (
                                        <Menu.Root
                                            trigger={
                                                <Button
                                                    variant="secondary"
                                                    size="md"
                                                >
                                                    Actions
                                                </Button>
                                            }
                                        >
                                            {skill.isEnabled ? (
                                                <Menu.Item
                                                    disabled={!canManage}
                                                    onClick={() =>
                                                        void disable(target)
                                                    }
                                                >
                                                    Disable
                                                </Menu.Item>
                                            ) : (
                                                <Menu.Item
                                                    disabled={!canManage}
                                                    onClick={() =>
                                                        void enable(target)
                                                    }
                                                >
                                                    Enable
                                                </Menu.Item>
                                            )}
                                            <Menu.Item
                                                destructive
                                                disabled={!canManage}
                                                onClick={() =>
                                                    requestDelete(target)
                                                }
                                            >
                                                Delete
                                            </Menu.Item>
                                        </Menu.Root>
                                    )}
                                </HStack>
                            </CommandIsland>
                        </div>
                    )}
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel
                    id={PANE_ID}
                    defaultSize="380px"
                    minSize={PANE_MIN_SIZE}
                    maxSize="45%"
                    collapsible
                    groupResizeBehavior="preserve-pixel-size"
                    panelRef={paneRef}
                    style={{ overflow: "hidden" }}
                    onResize={() =>
                        setIsPaneCollapsed(
                            paneRef.current?.isCollapsed() ?? false,
                        )
                    }
                >
                    <div className={styles.paneContent}>
                        <AgentSkillSidePanel
                            skill={skill}
                            tab={tab}
                            onTabChange={setTab}
                            canManage={canManage}
                            clientName={clientName}
                            restore={restore}
                            onUseDraft={openEditor}
                            onMoved={(id) => navigate(agentSkillPagePath(id))}
                        />
                    </div>
                </ResizablePanel>
            </ResizablePanelGroup>

            {deleteModal}
            {resetModal}
        </PageSection>
    );
};
